import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { SkillQuestionTypeEnumLabels } from 'src/app/enums/skill-question-type.enum';
import { ExamInstance, ExamInstanceItem, ExamInstanceItemRubricSnapshot } from 'src/app/models/exam_instance.model';

export interface ExamInstanceRubricMatrixCell {
  level_name: string;
  level_weight_percent: number;
  cell_weight_percent: number;
  rules: string;
}

export interface ExamInstanceRubricMatrixRow {
  dimension_name: string;
  dimension_weight_percent: number;
  cells: ExamInstanceRubricMatrixCell[];
}

export interface ExamInstanceItemDisplay extends ExamInstanceItem {
  type_label: string;
  minutes_label: string;
  options_display: string;
  sorted_rubric_levels: ExamInstanceItemRubricSnapshot['levels'];
  rubric_matrix_rows: ExamInstanceRubricMatrixRow[];
  has_rubric_matrix: boolean;
}

@Component({
  selector: 'app-exam-instance-editor',
  templateUrl: './exam-instance-editor.component.html',
  styleUrl: './exam-instance-editor.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatExpansionModule],
})
export class ExamInstanceEditorComponent {
  readonly exam_instance = input.required<ExamInstance | null>();

  readonly display_items = computed((): ExamInstanceItemDisplay[] => {
    const exam_instance = this.exam_instance();

    if (!exam_instance?.exam_instance_items) {
      return [];
    }

    const sorted_items = [...exam_instance.exam_instance_items].sort(
      (first_item, second_item) => first_item.sort_order - second_item.sort_order
    );

    return sorted_items.map(exam_instance_item => this.mapExamInstanceItemDisplay(exam_instance_item));
  });

  readonly total_exam_minutes = computed(() =>
    this.display_items().reduce((total_minutes, display_item) => {
      const item_minutes = display_item.approximate_minutes ?? 0;

      return total_minutes + item_minutes;
    }, 0)
  );

  readonly has_total_minutes = computed(() => this.total_exam_minutes() > 0);

  private mapExamInstanceItemDisplay(exam_instance_item: ExamInstanceItem): ExamInstanceItemDisplay {
    const rubric_snapshot = exam_instance_item.rubric_snapshot;
    const sorted_rubric_levels = rubric_snapshot
      ? [...rubric_snapshot.levels].sort((first_level, second_level) => first_level.sort_order - second_level.sort_order)
      : [];

    const rubric_matrix_rows = rubric_snapshot ? this.buildRubricMatrixRows(rubric_snapshot, sorted_rubric_levels) : [];

    return {
      ...exam_instance_item,
      type_label: SkillQuestionTypeEnumLabels[exam_instance_item.question_type] ?? exam_instance_item.question_type,
      minutes_label: this.buildMinutesLabel(exam_instance_item.approximate_minutes),
      options_display: this.buildOptionsDisplay(exam_instance_item),
      sorted_rubric_levels,
      rubric_matrix_rows,
      has_rubric_matrix: rubric_matrix_rows.length > 0 && sorted_rubric_levels.length > 0,
    };
  }

  private buildMinutesLabel(approximate_minutes: number | null): string {
    if (approximate_minutes === null || approximate_minutes === undefined) {
      return '—';
    }

    return `${approximate_minutes} min`;
  }

  private buildOptionsDisplay(exam_instance_item: ExamInstanceItem): string {
    const options_snapshot = exam_instance_item.options_snapshot ?? [];

    if (options_snapshot.length === 0) {
      return '—';
    }

    const sorted_options = [...options_snapshot].sort((first_option, second_option) => first_option.sort_order - second_option.sort_order);

    return sorted_options
      .map((option, option_index) => {
        const correct_marker = option.is_correct ? ' ✓' : '';

        return `${option_index + 1}. ${option.label}${correct_marker}`;
      })
      .join(' · ');
  }

  private buildRubricMatrixRows(
    rubric_snapshot: ExamInstanceItemRubricSnapshot,
    sorted_rubric_levels: ExamInstanceItemRubricSnapshot['levels']
  ): ExamInstanceRubricMatrixRow[] {
    return rubric_snapshot.dimensions.map((dimension, dimension_index) => ({
      dimension_name: dimension.name,
      dimension_weight_percent: dimension.weight_percent,
      cells: sorted_rubric_levels.map((level, level_index) => ({
        level_name: level.name,
        level_weight_percent: level.weight_percent,
        cell_weight_percent: Math.round((dimension.weight_percent * level.weight_percent) / 100),
        rules: this.findRubricRule(rubric_snapshot, dimension, level, dimension_index, level_index),
      })),
    }));
  }

  private findRubricRule(
    rubric_snapshot: ExamInstanceItemRubricSnapshot,
    dimension: ExamInstanceItemRubricSnapshot['dimensions'][number],
    level: ExamInstanceItemRubricSnapshot['levels'][number],
    dimension_index: number,
    level_index: number
  ): string {
    const dimension_levels = rubric_snapshot.dimension_levels ?? [];

    const matched_dimension_level = dimension_levels.find(dimension_level => {
      const matches_by_id =
        dimension.id !== undefined &&
        level.id !== undefined &&
        dimension_level.dimension_id === dimension.id &&
        dimension_level.level_id === level.id;

      const matches_by_index = dimension_level.dimension_index === dimension_index && dimension_level.level_index === level_index;

      return matches_by_id || matches_by_index;
    });

    const rules = matched_dimension_level?.rules?.trim();

    return rules && rules.length > 0 ? rules : '—';
  }
}
